import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Loader2 } from 'lucide-react';

export default function ControlLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useTrackOticAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { tenant } = await login(email, password);
      // Only SentinelX users can access Control Portal
      if (tenant?.code !== 'SENTINELX') {
        toast.error('Access denied. Control Portal is for SentinelX administrators only.');
        return;
      }
      toast.success('Welcome to Control Portal');
      navigate('/control/tenants');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">SentinelX Control Portal</h1>
          <p className="text-slate-400 mt-2">Platform Administration</p>
        </div>

        <div className="bg-slate-900 rounded-xl p-8 border border-slate-800">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@trackotic.com"
                className="bg-slate-800 border-slate-700 text-white"
                required
                data-testid="control-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-slate-800 border-slate-700 text-white"
                required
                data-testid="control-login-password"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="control-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In to Control Portal'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800 text-center">
            <p className="text-slate-500 text-sm">
              For tenant operations, use the{' '}
              <a href="/ops/login" className="text-blue-400 hover:underline">
                Customer Ops Portal
              </a>
            </p>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-8">
          Track<span className="text-red-500">0</span>tic by SentinelX Technology Pvt Ltd
        </p>
      </div>
    </div>
  );
}
